import { OrderObject } from '../helpers/objects/orderObject';
import styles from '../css/ordersPage.module.scss';
import { freezeScroll, unfreezeScroll } from '../helpers/environment';
import { useEffect } from 'react';
import CustomTextInput from './customTextInput';
import { useTranslation } from 'react-i18next';

const OrderDetailsModal = ({ order, isOpen, onClose }: { order: OrderObject; isOpen: boolean; onClose: () => void }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      freezeScroll();
    } else {
      unfreezeScroll();
    }
  }, [isOpen]);

  return (
    <div className={`${styles.orderDetailsModalContainer} ${isOpen ? styles.open : ''}`}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose}>
          <img src="/assets/images/close-icon.svg" alt="Close" />
        </button>
        <h1>{t('orderDetailsModal.title')}</h1>

        <hr />

        <h2>{t('orderDetailsModal.status')}</h2>

        <div className={styles.orderDetailsSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="status">{t('orderDetailsModal.status')}</label>
            <CustomTextInput type="text" id="status" disabled value={order?.status ?? t('orderDetailsModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="createdAt">{t('orderDetailsModal.createdAt')}</label>
            <CustomTextInput
              type="text"
              id="createdAt"
              disabled
              value={
                order?.createdAt
                  ? new Date(order.createdAt).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                    })
                  : t('orderDetailsModal.notAvailable')
              }
            />
          </div>
        </div>

        <hr />

        <h2>{t('orderDetailsModal.orderInformation')}</h2>

        <div className={styles.orderDetailsSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="firstName">{t('orderDetailsModal.firstName')}</label>
            <CustomTextInput type="text" id="firstName" disabled value={order?.user?.firstName ?? t('orderDetailsModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="lastName">{t('orderDetailsModal.lastName')}</label>
            <CustomTextInput type="text" id="lastName" disabled value={order?.user?.lastName ?? t('orderDetailsModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="email">{t('orderDetailsModal.email')}</label>
            <CustomTextInput type="email" id="email" disabled value={order?.user?.email ?? t('orderDetailsModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="phoneNumber">{t('orderDetailsModal.phoneNumber')}</label>
            <CustomTextInput type="text" id="phoneNumber" disabled value={order?.user?.phoneNumber ?? t('orderDetailsModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="country">{t('orderDetailsModal.country')}</label>
            <CustomTextInput type="text" id="country" disabled value={order?.user?.country ?? t('orderDetailsModal.notAvailable')} />
          </div>
        </div>
        <hr />
        {order?.purchaseInformation?.otherPassengers?.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <h2>{t('orderDetailsModal.additionalMembers')}</h2>
            {order?.purchaseInformation?.otherPassengers?.map((member, index) => (
              <div key={member.firstName + index} style={{ width: '100%' }}>
                <div className={styles.orderDetailsSection}>
                  <div className={styles.inputContainer}>
                    <label htmlFor="firstName">{t('orderDetailsModal.firstName')}</label>
                    <CustomTextInput type="text" id="firstName" disabled value={member?.firstName ?? t('orderDetailsModal.notAvailable')} />
                  </div>
                  <div className={styles.inputContainer}>
                    <label htmlFor="lastName">{t('orderDetailsModal.lastName')}</label>
                    <CustomTextInput type="text" id="lastName" disabled value={member?.lastName ?? t('orderDetailsModal.notAvailable')} />
                  </div>
                  <div className={styles.inputContainer}>
                    <label htmlFor="email">{t('orderDetailsModal.email')}</label>
                    <CustomTextInput type="email" id="email" disabled value={member?.email ?? t('orderDetailsModal.notAvailable')} />
                  </div>
                </div>
                <hr />
              </div>
            ))}
          </div>
        )}

        <h2>{t('orderDetailsModal.planDetails')}</h2>
        <div className={styles.orderDetailsSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="planName">{t('orderDetailsModal.planName')}</label>
            <CustomTextInput type="text" id="planName" disabled value={order?.plan?.product?.name ?? t('orderDetailsModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="planPrice">{t('orderDetailsModal.planPrice')}</label>
            <CustomTextInput type="text" id="planPrice" disabled value={order?.plan?.price ?? t('orderDetailsModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="familyPlan">{t('orderDetailsModal.familyPlan')}</label>
            <CustomTextInput type="text" id="familyPlan" disabled value={order?.plan?.product?.family?.toString() ?? t('orderDetailsModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="perTicketPlan">{t('orderDetailsModal.perPlanTicket')}</label>
            <CustomTextInput type="text" id="perTicketPlan" disabled value={order?.plan?.product?.singleFlight?.toString() ?? t('orderDetailsModal.notAvailable')} />
          </div>
        </div>

        <hr />

        <h2>{t('orderDetailsModal.activationInformation')}</h2>
        <div className={styles.orderDetailsSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="departureAirport">{t('orderDetailsModal.departureAirport')}</label>
            <CustomTextInput
              type="text"
              id="departureAirport"
              disabled
              value={order?.activationInfo?.departureAirport?.fullName && order?.activationInfo?.departureAirport?.IATA ? order?.activationInfo?.departureAirport?.fullName + ` (${order?.activationInfo?.departureAirport?.IATA})` : t('orderDetailsModal.notAvailable')}
            />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="arrivalAirport">{t('orderDetailsModal.arrivalAirport')}</label>
            <CustomTextInput
              type="text"
              id="arrivalAirport"
              disabled
              value={order?.activationInfo?.arrivalAirport?.fullName && order?.activationInfo?.arrivalAirport?.IATA ? order?.activationInfo?.arrivalAirport?.fullName + ` (${order?.activationInfo?.arrivalAirport?.IATA})` : t('orderDetailsModal.notAvailable')}
            />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="airline">{t('orderDetailsModal.airline')}</label>
            <CustomTextInput
              type="text"
              id="airline"
              disabled
              value={order?.activationInfo?.airline?.fullName && order?.activationInfo?.airline?.IATA ? order?.activationInfo?.airline?.fullName + ` (${order?.activationInfo?.airline?.IATA})` : t('orderDetailsModal.notAvailable')}
            />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="flightNumber">{t('orderDetailsModal.flightNumber')}</label>
            <CustomTextInput type="text" id="flightNumber" disabled value={order?.activationInfo?.flightNumber ?? t('orderDetailsModal.notAvailable')} />
          </div>
        </div>

        <h2>{t('orderDetailsModal.skycopInfo')}</h2>
        <div className={styles.orderDetailsSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="code">{t('orderDetailsModal.code')}</label>
            <CustomTextInput type="text" id="code" disabled value={order?.skycopAssignedInfo?.code ?? t('orderDetailsModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="expiringDate">{t('orderDetailsModal.expiringDate')}</label>
            <CustomTextInput
              type="text"
              id="expiringDate"
              disabled
              value={
                order?.skycopAssignedInfo?.expiringDate?.date
                  ? new Date(order?.skycopAssignedInfo?.expiringDate?.date).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                    })
                  : t('orderDetailsModal.notAvailable')
              }
            />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="skycopId">{t('orderDetailsModal.id')}</label>
            <CustomTextInput type="text" id="skycopID" disabled value={order?.skycopAssignedInfo?.id ?? t('orderDetailsModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="skycopType">{t('orderDetailsModal.type')}</label>
            <CustomTextInput type="text" id="skycopType" disabled value={order?.skycopAssignedInfo?.type ?? t('orderDetailsModal.notAvailable')} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
